import { ColorTypes, utils } from "@omegagrid/core";
import { html, TemplateResult } from "lit";
import { ToolbarItemOptions } from "../types";
import { CodeEditorAdapter, CodeEditorOptions, ContentOptions } from "./codeEditorAdapter";
import { ScriptEditor } from "./scriptEditor";

export type ScriptEditorOptions = CodeEditorOptions & {
	toolbar?: {
		save?: ToolbarItemOptions,
		discard?: ToolbarItemOptions,
		exec?: ToolbarItemOptions,
		stop?: ToolbarItemOptions,
	}
}

const DEFAULT_SCRIPT_EDITOR_OPTIONS: ScriptEditorOptions = {
	language: 'js',
	toolbar: {
		exec: {
			ord: 0,
			color: ColorTypes.Gray,
			icon: {icon: ['fas', 'play'], color: '#00d100'},
			enabled: true,
			type: 'button',
			id: 'exec',
			text: 'Execute',
			title: '(Ctrl+B)',
			width: 80,
		},
		stop: {
			type: 'button',
			hidden: true,
			ord: 1,
			title: 'Stop',
			color: ColorTypes.Yellow,
			content: html`
				<div slot="content" class="rp-editor-stop" style="white-space: nowrap; padding: 0 4px">
					<style>
						.rp-editor-stop og-loader { display: inline-block; }
						.rp-editor-stop og-icon { display: none; color: var(--og-colortype-red-background-color, #ef5350); }
						og-toolbar-button:hover .rp-editor-stop og-loader { display: none; }
						og-toolbar-button:hover .rp-editor-stop og-icon { display: inline-flex; }
					</style>
					<og-loader icon="gear"></og-loader>
					<og-icon .icon="${{ icon: ['fas', 'stop'] }}"></og-icon>
					<span id="duration"></span>
				</div>
			`,
			enabled: true
		},
		save: {
			ord: 2,
			type: 'button',
			text: 'Save changes',
			title: '(Ctrl+S)',
			color: ColorTypes.Green,
			icon: 'save',
			enabled: true
		},
		discard: {
			ord: 3,
			type: 'button',
			text: 'Discard changes',
			color: ColorTypes.Red,
			icon: 'floppy-disk-circle-xmark',
			enabled: true
		}
	}
}

export type ScriptResultType = 'error' | 'info' | 'success';
export type ScriptResult = {
    type?: ScriptResultType;
    text?: string;
};

export type ScriptExecCallbackArgs = {
	result?: ScriptResult;
};

export abstract class ScriptEditorAdapter extends CodeEditorAdapter<ScriptEditor> {
	public options: ScriptEditorOptions = {};

	abstract getContent(opt?: ContentOptions) : string|Promise<string>;
	abstract save(code: string) : void|Promise<void>;
	
	exec(code: string, cb?: (args: ScriptExecCallbackArgs) => void) : void|Promise<void> {
		console.info('exec not implemented');
	}
	
	abort() : void|Promise<void> {
		console.info('abort not implemented');
	}

	createOptions(options: Partial<ScriptEditorOptions>): ScriptEditorOptions {
		const mergedOptions = utils.mergeDeep({}, DEFAULT_SCRIPT_EDITOR_OPTIONS, options);
		// fix HTML template after merge
		if (options.toolbar?.stop?.content) {
			mergedOptions.toolbar.stop.content = options.toolbar.stop.content;
		} else if (DEFAULT_SCRIPT_EDITOR_OPTIONS.toolbar?.stop?.content) {
			mergedOptions.toolbar.stop.content = DEFAULT_SCRIPT_EDITOR_OPTIONS.toolbar.stop.content;
		}
		
		return this.options = mergedOptions;
	}

	getOptions() : ScriptEditorOptions|Promise<ScriptEditorOptions> {
		return DEFAULT_SCRIPT_EDITOR_OPTIONS;
	}

}
